import React, { useEffect, useState, useRef } from "react";
import { Button, Row } from "react-bootstrap";
import "./BillingPage.css";
import instance, {
  apiEncryptRequest,
  currencyReturn,
  currencyReturnOnlyAmount,
  decryptData,
  getCurrencySymbol,
} from "../../Api";
import { useAuth } from "../../AuthContext";
import Loader from "../common/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

// ── Helpers ───────────────────────────────────────────────────────────────────
const isMobileDevice = () => window.matchMedia("(max-width: 800px)").matches;

// Disk value helper used in table cells and export
const diskVal = (item) =>
  item.disk_type === "hdd" ? item.hard_disk : item.disk_type === "ssd" ? item.ssd : item.nvme;

// ── Shared sub-components ─────────────────────────────────────────────────────

// Table header cell (top image row) — same structure for every column
const TableHead = ({ children }) => (
  <div className="table-head">
    <div className="table-content">{children}</div>
    <img src="/images/admin/08-VM-Billing-List/server-top-img.svg" className="bg-image" alt="Background" />
  </div>
);

// Table data cell (middle image row)
const TableData = ({ children }) => (
  <div className="table-data">
    <div className="table-content">{children}</div>
    <img src="/images/admin/08-VM-Billing-List/server-middle-img.svg" className="bg-image" alt="Background" />
  </div>
);

// Footer cell (bottom image)
const TableFoot = () => (
  <div className="table-foot">
    <img src="/images/admin/09-Billing-Table/server-bottom-bg.svg" className="table-footer-image-size" alt="Background" />
  </div>
);

// ── SERVER billing table — same markup used on mobile + web, no duplication ──
const ServerTable = ({ data, smuser, appCurrency }) => (
  <div className="billing-list">
    {/* Header */}
    <div className="table-row no-hover">
      <TableHead><div>Action Performed</div><div style={{ fontSize:"14px" }}>Action Time</div></TableHead>
      <TableHead><div>Previous Action</div><div style={{ fontSize:"14px" }}>Action Time</div></TableHead>
      <TableHead><div>Previous Configuration</div><div style={{ fontSize:"14px" }}>RAM/DISK/vCPU</div></TableHead>
      <TableHead><div>Present Configuration</div><div style={{ fontSize:"14px" }}>RAM/DISK/vCPU</div></TableHead>
      <TableHead>
        <div>Cost</div>
        <div style={{ fontSize:"14px" }}>
          (in {smuser?.prefer_currency}) ({getCurrencySymbol(smuser?.prefer_currency)})
        </div>
      </TableHead>
    </div>

    {/* Rows */}
    {data && data.map((item, idx) => {
      const disk = diskVal(item);
      const config = `${item.ram / 1024} GB / ${disk} GB / ${item.cpu} vCPU`;
      const isDelete = item.vm_action === "DELETE";
      const price = isDelete ? item.wallet_comes : item.linode_rate;
      return (
        <div className="table-row" key={idx}>
          <TableData>
            <div>{item.vm_action}</div>
            <div style={{ fontSize:"14px" }}>{item.action_time}</div>
            {isDelete && <div style={{ fontSize:"14px" }}>(added to wallet)</div>}
          </TableData>
          <TableData>
            <div>{item.vm_action}{isDelete ? "1" : ""}</div>
            <div style={{ fontSize:"14px" }}>{item.created_at}</div>
            {isDelete && <div style={{ fontSize:"14px" }}>(added to wallet)</div>}
          </TableData>
          <TableData>{config}</TableData>
          <TableData><div>{config}</div></TableData>
          <TableData>
            <div>
              <strong>
                <i className="fas fa-rupee-sign" style={{ paddingTop:"3px" }} aria-hidden="true" />
                <span>
                  {isDelete ? "+ " : "- "}
                  {appCurrency && smuser && currencyReturnOnlyAmount({ price, symbol: smuser.prefer_currency, rates: appCurrency })}
                </span>
              </strong>
            </div>
          </TableData>
        </div>
      );
    })}

    {/* Footer */}
    <div className="table-row no-hover">
      {[0,1,2,3,4].map((i) => <TableFoot key={i} />)}
    </div>
  </div>
);

// ── CDN billing table ─────────────────────────────────────────────────────────
const CDNTable = ({ data, smuser, appCurrency }) => (
  <div className="billing-list">
    {/* Header */}
    <div className="table-row no-hover">
      <TableHead><div>Action Performed</div><div style={{ fontSize:"14px" }}>Action Time</div></TableHead>
      <TableHead><div>Previous Action</div><div style={{ fontSize:"14px" }}>Action Time</div></TableHead>
      <TableHead><div>Total Data</div></TableHead>
      <TableHead><div>Remain Data</div></TableHead>
      <TableHead>
        <div>Cost</div>
        <div style={{ fontSize:"14px" }}>
          (in {smuser?.prefer_currency}) ({getCurrencySymbol(smuser?.prefer_currency)})
        </div>
      </TableHead>
    </div>

    {/* Rows */}
    {data && data.map((item, idx) => {
      const isDelete = item.vm_action === "DELETE";
      const price = isDelete ? item.wallet_comes : item.linode_rate;
      return (
        <div className="table-row" key={idx}>
          <TableData>
            <div>{item.vm_action}</div>
            <div style={{ fontSize:"14px" }}>{item.action_time}</div>
            {isDelete && <div style={{ fontSize:"14px" }}>(added to wallet)</div>}
          </TableData>
          <TableData>
            <div>{item.vm_action}</div>
            <div style={{ fontSize:"14px" }}>{item.created_at}</div>
            {isDelete && <div style={{ fontSize:"14px" }}>(added to wallet)</div>}
          </TableData>
          <TableData>{item.cdn_datatransfer} TB</TableData>
          <TableData><div>{item.remaining_data} TB</div></TableData>
          <TableData>
            <div>
              <strong>
                <i className="fas fa-rupee-sign" style={{ paddingTop:"3px" }} aria-hidden="true" />
                <span>
                  {isDelete ? "+ " : "- "}
                  {appCurrency && smuser && currencyReturnOnlyAmount({ price, symbol: smuser.prefer_currency, rates: appCurrency })}
                </span>
              </strong>
            </div>
          </TableData>
        </div>
      );
    })}

    {/* Footer */}
    <div className="table-row no-hover">
      {[0,1,2,3,4].map((i) => <TableFoot key={i} />)}
    </div>
  </div>
);

// ── Date filter toolbar — shared between SERVER and CDN panels ────────────────
const FilterBar = ({ name, start, end, onStart, onEnd, onSubmit, onExport, mobile }) => (
  <div style={{ position:"relative", backgroundColor:"#e97730", padding:"18px", borderRadius:"20px", zIndex:"999",
    width: mobile ? "88%" : "70%",
    marginLeft: mobile ? "20px" : "9rem",
    marginTop: mobile ? "2rem" : "60px" }}>
    <div style={{ display: mobile ? undefined : "flex", position:"relative", zIndex:"1" }}>
      <input className="input" type="text" value={name} readOnly
        style={{ borderRadius: mobile ? "30px" : "15px", paddingLeft:"10px",
          marginLeft: mobile ? undefined : "10px",
          padding: mobile ? "10px 15px" : undefined,
          border: mobile ? "2px solid #e97730" : undefined,
          marginRight:"15px", fontSize: mobile ? "16px" : undefined }}
        onFocus={onStart.clear} />
      <div className="search-form" style={{ marginTop: mobile ? "10px" : "10px", marginLeft: mobile ? undefined : "20px" }}>
        <div className="input-container-notif" style={mobile ? { width:"17rem" } : undefined}>
          <input type="date" name="from" className="input-signup" placeholder="From"
            value={start} onChange={(e) => onStart(e.target.value)} />
        </div>
        <div className="input-container-notif" style={mobile ? { width:"17rem" } : undefined}>
          <input type="date" name="to" className="input-signup" placeholder="To"
            value={end} onChange={(e) => onEnd(e.target.value)} />
        </div>
        <input type="hidden" id="getval" value="All" />
        <div className="button-fields">
          <div className="button-cont" style={{ marginTop:"-11px" }}>
            <input type="submit" name="submit" value="Submit" className="input-submit new-btn" onClick={onSubmit} />
          </div>
          <div className="button-cont" style={{ marginTop:"-11px" }}>
            <input type="export" name="export" value="Export" readOnly className="input-submit new-btn-export"
              style={{ paddingLeft: mobile ? undefined : "23px", fontWeight:"700", color:"#154e7a",
                backgroundColor:"white", marginLeft:"10px", width: mobile ? "7rem" : "6rem",
                borderRadius:"50px", border:"1px solid rgb(255, 255, 255)" }}
              onClick={onExport} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const BillReport = () => {
  const { smuser, appCurrency } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const [loading, setLoading]         = useState(true);
  const [isMobile, setIsMobile]       = useState(isMobileDevice);
  const [billingData, setBillingData] = useState([]);
  const [allVMS, setAllVMS]           = useState(null);
  const [cdnBillingData, setCDNBillingData] = useState([]);
  const [allCDN, setAllCDN]           = useState([]);
  const [activeId, setActiveID]       = useState(null);

  const billData       = location.state?.billData       ?? null;
  const isServerOrCDN  = location.state?.isServerOrCDN  ?? null;

  const [activeButton, setActiveButton] = useState("SERVER");
  const tabs = ["SERVER", "CDN"];

  // Search / dropdown state (UI currently commented out but logic preserved)
  const [showvm, setShowvm]           = useState("all");
  const [visible, setVisible]         = useState(false);
  const [searchText, setSearchText]   = useState("");
  const [selectedVM, setSelectedVM]   = useState(null);
  const [visibleCDN, setVisibleCDN]   = useState(false);
  const [showCDN, setShowCDN]         = useState("all");
  const [searchTextCDN, setSearchTextCDN] = useState("");
  const [selectedCDN, setSelectedCDN] = useState(null);

  // Date filter state — SERVER
  const [startDate, setStartDate]           = useState("");
  const [endDate, setEndDate]               = useState("");
  const [filteredServerData, setFilteredServerData] = useState([]);

  // Date filter state — CDN
  const [startDatecdn, setStartDateCDN]     = useState("");
  const [endDatecdn, setEndDateCDN]         = useState("");
  const [filteredCDNData, setFilteredCDNData] = useState([]);

  // Fix: original had [isMobile] in deps — BillingCall() re-fired on every
  // resize. Resize is handled by the listener; isMobile removed from deps.
  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveButton(isServerOrCDN);
    if (!billData) { navigate(-1); return; }
    setActiveID(billData.vm_id);
    BillingCall();
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── API calls ──────────────────────────────────────────────────────────────
  const BillingCall = async () => {
    setLoading(true);
    try {
      const enc = await apiEncryptRequest({ user_id: smuser.id, vm_id: billData.vm_id, cdn_id: billData.id });
      const res = await instance.post("/bill/bill_reports", enc);
      const data = await decryptData(res.data);

      const billsArr = Object.values(data.bills);
      setBillingData(billsArr);
      setFilteredServerData(billsArr);
      setSearchText(billData.vm_name);
      setShowvm(billsArr.vm_id);

      setCDNBillingData(data.cdnbills);
      setFilteredCDNData(data.cdnbills);

      if (data.vms)  setAllVMS(Object.values(data.vms));
      if (data.cdn)  setAllCDN(Object.values(data.cdn));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleChangeMachine = () => {
    if (activeId !== null) { setBillingData(null); UpdateTable(); }
  };

  const UpdateTable = async () => {
    setLoading(true);
    try {
      const enc = await apiEncryptRequest({ user_id: smuser.id, vm_id: activeId });
      const res = await instance.post("/bill/bill_reports", enc);
      const data = await decryptData(res.data);
      setBillingData(data.bills);
      setAllVMS(Object.values(data.vms));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Dropdown / search helpers (UI commented out, logic preserved)
  const handleClick = (e) => {
    if (dropdownRef.current?.contains(e.target)) return;
    setVisible(false); setVisibleCDN(false);
  };
  const handleChange    = (e) => { setSearchText(e.target.value); if (!e.target.value) setShowvm("all"); if (!visible) setVisible(true); };
  const handleChangeCDN = (e) => { setSearchTextCDN(e.target.value); if (!e.target.value) setShowCDN("all"); if (!visibleCDN) setVisibleCDN(true); };
  const selectItem      = (item) => { setSearchText(item.vm_name); setSelectedVM(item.vm_id); setShowvm(item.vm_id); setVisible(false); };
  const selectItemCDN   = (item) => { setSearchTextCDN(item.website_url); setSelectedCDN(item.id); setShowCDN(item.id); setVisibleCDN(false); };
  const searchFilter    = (val, list, by = "vm_name")      => val ? list.filter((x) => x[by].toLowerCase().includes(val.toLowerCase())) : list;
  const searchFilterCDN = (val, list, by = "website_url")  => val ? list.filter((x) => x[by].toLowerCase().includes(val.toLowerCase())) : list;

  // ── Date filtering ─────────────────────────────────────────────────────────
  const filterByDate = (source, start, end) =>
    source.filter((item) => {
      const d = new Date(item.created_at); d.setHours(0,0,0,0);
      const s = new Date(start);           s.setHours(0,0,0,0);
      const e = new Date(end);             e.setHours(0,0,0,0);
      return d >= s && d <= e;
    });

  const filterServerByDate = () => setFilteredServerData(filterByDate(billingData, startDate, endDate));
  const filterCDNbyDate    = () => setFilteredCDNData(filterByDate(cdnBillingData, startDatecdn, endDatecdn));

  // ── XLSX exports ───────────────────────────────────────────────────────────
  const exportToExcelServer = (data) => {
    const rows = data.map((item) => {
      const isDelete = item.vm_action === "DELETE";
      const config = item.disk_type !== null
        ? `${item.ram / 1024} GB / ${diskVal(item)} GB / ${item.cpu} vCPU(s)`
        : `${item.ram / 1024} GB / ${item.hard_disk} GB / ${item.cpu} vCPU`;
      return {
        server_name: billData.vm_name,
        Action_performed: `${item.vm_action}\n${item.action_time}\n${isDelete ? "(added to wallet)" : ""}`,
        Previous_action:  `${item.vm_action}\n${item.created_at}\n${isDelete ? "(added to wallet)" : ""}`,
        Previous_configuration: config,
        Present_configuration:  config,
        cost: appCurrency && smuser ? currencyReturn({ price: isDelete ? item.wallet_comes : item.linode_rate, symbol: smuser.prefer_currency, rates: appCurrency }) : "",
      };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Sheet1");
    XLSX.writeFile(wb, "billingData.xlsx");
  };

  const exportToExcelCDN = (data) => {
    const rows = data.map((item) => {
      const isDelete = item.vm_action === "DELETE";
      return {
        cdn_name:         billData.website_url,
        Action_performed: item.vm_action,
        Previous_action:  item.vm_action,
        total_data:       item.cdn_datatransfer,
        remain_data:      item.remaining_data,
        amount: appCurrency && smuser ? currencyReturn({ price: isDelete ? item.wallet_comes : item.linode_rate, symbol: smuser.prefer_currency, rates: appCurrency }) : "",
      };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Sheet1");
    XLSX.writeFile(wb, "cdn_Data.xlsx");
  };

  // ── Tab buttons (shared) ───────────────────────────────────────────────────
  const TabButtons = ({ style }) => (
    <div style={style}>
      {tabs.map((title, idx) => (
        <Button key={idx}
          style={{ background: activeButton === title ? "#f47c20" : "#035189", border:"none", fontSize:"20px",
            padding:"5px 15px", color:"#fff", fontWeight:"600", borderRadius:"10px", marginBottom:"10px" }}
          onClick={() => setActiveButton(title)}>{title}</Button>
      ))}
    </div>
  );

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ width:"100%", minHeight:"65rem", position:"relative",
      backgroundImage:"url(./main-bg.jpg)", backgroundSize:"cover",
      backgroundRepeat:"round", backgroundBlendMode:"overlay" }}>

      {isMobile ? (
        /* ── MOBILE ── */
        <div style={{ height:"50rem" }}>
          <div className="heading-dotted-bill">VM Billing List <span/></div>
          <TabButtons style={{ position:"absolute", marginLeft:"15px", marginTop:"1rem" }}/>

          <div className="features-section-solution">
            {activeButton === "SERVER" && (
              <Row>
                <FilterBar mobile
                  name={billData.vm_name}
                  start={startDate} end={endDate}
                  onStart={(v) => setStartDate(v)}
                  onEnd={(v) => setEndDate(v)}
                  onSubmit={filterServerByDate}
                  onExport={() => exportToExcelServer(filteredServerData)}
                />
                <div className="col-md-12">
                  <div style={{ display:"flex", position:"relative", marginTop:"2rem", maxWidth:"100%", overflowX:"auto", whiteSpace:"nowrap" }}>
                    <ServerTable data={billingData} smuser={smuser} appCurrency={appCurrency} />
                  </div>
                </div>
              </Row>
            )}

            {activeButton === "CDN" && (
              <Row>
                <FilterBar mobile
                  name={billData.website_url}
                  start={startDatecdn} end={endDatecdn}
                  onStart={(v) => setStartDateCDN(v)}
                  onEnd={(v) => setEndDateCDN(v)}
                  onSubmit={filterCDNbyDate}
                  onExport={() => exportToExcelCDN(filteredCDNData)}
                />
                <div className="col-md-12">
                  <div style={{ display:"flex", position:"relative", marginTop:"2rem", maxWidth:"100%", overflowX:"auto", whiteSpace:"nowrap" }}>
                    <CDNTable data={filteredCDNData} smuser={smuser} appCurrency={appCurrency} />
                  </div>
                </div>
              </Row>
            )}
          </div>
        </div>
      ) : (
        /* ── WEB ── */
        <div className="features-page-solution" style={{ height:"50rem", paddingLeft:"7rem", paddingTop:"4rem" }}>
          <div className="heading-dotted-bill">VM Billing List <span/></div>
          <TabButtons style={{ position:"absolute", marginLeft:"9rem", marginTop:"1rem" }}/>

          {activeButton === "SERVER" && (
            <div className="features-section-solution">
              <Row>
                <FilterBar
                  name={billData.vm_name}
                  start={startDate} end={endDate}
                  onStart={(v) => setStartDate(v)}
                  onEnd={(v) => setEndDate(v)}
                  onSubmit={filterServerByDate}
                  onExport={() => exportToExcelServer(filteredServerData)}
                />
                <div className="col-md-12">
                  <div style={{ display:"flex", position:"relative", marginLeft:"8rem", marginTop:"2rem", maxWidth:"100%", overflowX:"auto", whiteSpace:"nowrap" }}>
                    <ServerTable data={filteredServerData} smuser={smuser} appCurrency={appCurrency} />
                  </div>
                </div>
              </Row>
            </div>
          )}

          {activeButton === "CDN" && (
            <div className="features-section-solution">
              <Row>
                <FilterBar
                  name={billData.website_url}
                  start={startDatecdn} end={endDatecdn}
                  onStart={(v) => setStartDateCDN(v)}
                  onEnd={(v) => setEndDateCDN(v)}
                  onSubmit={filterCDNbyDate}
                  onExport={() => exportToExcelCDN(filteredCDNData)}
                />
                <div className="col-md-12">
                  <div style={{ display:"flex", position:"relative", marginLeft:"8rem", marginTop:"2rem", maxWidth:"100%", overflowX:"auto", whiteSpace:"nowrap" }}>
                    <CDNTable data={filteredCDNData} smuser={smuser} appCurrency={appCurrency} />
                  </div>
                </div>
              </Row>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="loading-overlay" style={{ zIndex:"9999999999999999" }}>
          <Loader isLoading={loading} />
        </div>
      )}
    </div>
  );
};

export default BillReport;